import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/auth/api';
import type { DnsChangeLog } from '@breaker/shared-types';

/**
 * Unit B — DNS & email management on a managed domain. Client half of the
 * preview/apply API. Every mutation is a single `DnsAction`; the server
 * recomputes the plan from the live zone, so the client only ever sends
 * intent.
 */

export type EmailPresetId = 'google-workspace' | 'microsoft-365' | 'other';
export type EditableRecordType = 'A' | 'AAAA' | 'CNAME' | 'TXT' | 'MX';

export interface DnsRecordView {
  type: string;
  name: string;
  ttl: number | null;
  group: 'custom' | 'product';
  data: Record<string, unknown>;
  editable: boolean;
}

export interface EmailPosture {
  preset: EmailPresetId | null;
  mx: Array<{ exchange: string; preference: number }>;
  spf: string | null;
}

export interface DnsOverview {
  domain: string;
  records: DnsRecordView[];
  email: EmailPosture;
}

export interface RecordRef {
  type: string;
  name: string;
  value: string;
}

export interface CustomRecordInput {
  type: EditableRecordType;
  name: string;
  value: string;
  ttl?: number;
  preference?: number;
}

export type DnsAction =
  | {
      kind: 'email_preset';
      preset: EmailPresetId;
      other?: { mx: Array<{ exchange: string; preference: number }>; spfInclude?: string };
    }
  | { kind: 'custom_upsert'; record: CustomRecordInput; replaces?: RecordRef }
  | { kind: 'custom_delete'; record: RecordRef };

export interface DnsPlan {
  kind: 'email_preset' | 'custom_upsert' | 'custom_delete';
  summary: string;
  toSave: Array<Record<string, unknown>>;
  toDelete: RecordRef[];
  spf?: string;
}

export interface PreviewResult {
  plan: DnsPlan;
  noop: boolean;
}

export interface ApplyResult {
  noop: boolean;
  plan: DnsPlan;
  records: DnsRecordView[];
}

const base = (fleetId: string, domain: string) =>
  `/v1/fleets/${fleetId}/domains/${encodeURIComponent(domain)}/dns`;

const overviewKey = (fleetId: string | null, domain: string | null) =>
  ['fleets', fleetId ?? '', 'domains', domain ?? '', 'dns'] as const;
const changesKey = (fleetId: string | null, domain: string | null) =>
  ['fleets', fleetId ?? '', 'domains', domain ?? '', 'dns', 'changes'] as const;

export function useDomainDns(fleetId: string | null, domain: string | null) {
  return useQuery({
    queryKey: overviewKey(fleetId, domain),
    enabled: Boolean(fleetId && domain),
    queryFn: () => api<DnsOverview>(base(fleetId!, domain!)),
  });
}

export function useDomainDnsChanges(fleetId: string | null, domain: string | null) {
  return useQuery({
    queryKey: changesKey(fleetId, domain),
    enabled: Boolean(fleetId && domain),
    queryFn: () => api<DnsChangeLog[]>(`${base(fleetId!, domain!)}/changes`),
  });
}

export function usePreviewDnsChange(fleetId: string | null, domain: string | null) {
  return useMutation({
    mutationFn: (action: DnsAction) =>
      api<PreviewResult>(`${base(fleetId!, domain!)}/preview`, {
        method: 'POST',
        body: { action },
      }),
  });
}

export function useApplyDnsChange(fleetId: string | null, domain: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (action: DnsAction) =>
      api<ApplyResult>(`${base(fleetId!, domain!)}/apply`, {
        method: 'POST',
        body: { action },
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: overviewKey(fleetId, domain) });
      qc.invalidateQueries({ queryKey: changesKey(fleetId, domain) });
    },
  });
}

/** Human-readable value of a record view, by type. */
export function recordValue(r: Pick<DnsRecordView, 'type' | 'data'>): string {
  const d = r.data;
  const s = (k: string) => (typeof d[k] === 'string' ? (d[k] as string) : '');
  switch (r.type.toUpperCase()) {
    case 'A':
    case 'AAAA':
      return s('address');
    case 'CNAME':
      return s('cname');
    case 'TXT':
      return s('value');
    case 'MX':
      return `${typeof d['preference'] === 'number' ? d['preference'] : ''} ${s('exchange')}`.trim();
    default:
      return Object.values(d).find((v) => typeof v === 'string') as string | undefined ?? '';
  }
}
